import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from '../../contexts/LanguageContext';
import { Calculator, TrendingDown, TrendingUp, Users, Info } from "lucide-react";

const TotalExpensesCard = ({ 
  total, 
  originalTotal,
  activeSubscriptions = 0, 
  freeTrials = [],
  estimatedExpense = {
    net_adjustment: 0,
    total_savings: 0,
    total_additional_cost: 0,
    is_net_saving: true,
    spaces_count: 0,
    summary: ''
  }
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);
  const [showExpenseDetails, setShowExpenseDetails] = useState(false);
  
  // Calculate total number of active free trials
  const activeFreeTrialsCount = freeTrials.filter(trial => 
    !trial.cancelled
  ).length;
  
  const avgCost = activeSubscriptions > 0 ? (originalTotal / activeSubscriptions) : 0;

  // Calculate annual and monthly values
  const annualMultiplier = 12;
  
  // Original personal subscriptions (monthly)
  const annualOriginalTotal = originalTotal * annualMultiplier;
  const annualAvgCost = avgCost * annualMultiplier;
  
  // Estimated expense adjustments (monthly -> convert to annual if needed)
  const monthlyNetAdjustment = estimatedExpense.net_adjustment;
  const annualNetAdjustment = monthlyNetAdjustment * annualMultiplier;
  
  const monthlyTotalSavings = estimatedExpense.total_savings;
  const annualTotalSavings = monthlyTotalSavings * annualMultiplier;
  
  const monthlyTotalAdditionalCost = estimatedExpense.total_additional_cost;
  const annualTotalAdditionalCost = monthlyTotalAdditionalCost * annualMultiplier;
  
  // Current values based on view (monthly/annual)
  const currentTotal = isAnnual ? total * annualMultiplier : total;
  const currentOriginalTotal = isAnnual ? annualOriginalTotal : originalTotal;
  const currentAvgCost = isAnnual ? annualAvgCost : avgCost;
  const currentNetAdjustment = isAnnual ? annualNetAdjustment : monthlyNetAdjustment;
  const currentTotalSavings = isAnnual ? annualTotalSavings : monthlyTotalSavings;
  const currentTotalAdditionalCost = isAnnual ? annualTotalAdditionalCost : monthlyTotalAdditionalCost;
  
  const hasNetSaving = currentNetAdjustment > 0;
  const hasNetCost = currentNetAdjustment < 0;
  const absoluteAdjustment = Math.abs(currentNetAdjustment);

  // Format currency
  const formatCurrency = (amount) => {
    return `€${Math.abs(amount).toFixed(2)}`;
  };

  // Get sharing spaces count
  const sharingSpacesCount = estimatedExpense.spaces_count || 0;

  // Simple helper function to get the summary text without complex interpolation
  const getSummaryText = () => {
    if (sharingSpacesCount === 0) return "";
    
    if (hasNetSaving) {
      if (sharingSpacesCount === 1) {
        return t('dashboard.totalExpensesCard.savingSummarySingle', 'Net estimated saving from 1 sharing space');
      } else {
        // Simple string replacement for count
        return t('dashboard.totalExpensesCard.savingSummaryMultiple', 'Net estimated saving from {count} sharing spaces')
          .replace('{count}', sharingSpacesCount);
      }
    } else {
      if (sharingSpacesCount === 1) {
        return t('dashboard.totalExpensesCard.costSummarySingle', 'Net estimated cost from 1 sharing space');
      } else {
        // Simple string replacement for count
        return t('dashboard.totalExpensesCard.costSummaryMultiple', 'Net estimated cost from {count} sharing spaces')
          .replace('{count}', sharingSpacesCount);
      }
    }
  };

  const handleManageClick = () => {
    navigate("/share-subs");
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium text-gray-800">
            {isAnnual 
              ? t('dashboard.totalExpensesCard.totalAnnualExpenses', 'Total Annual Expenses')
              : t('dashboard.totalExpensesCard.totalMonthlyExpenses', 'Total Monthly Expenses')
            }
          </h3>
          {currentNetAdjustment !== 0 && (
            <button
              onClick={() => setShowExpenseDetails(!showExpenseDetails)}
              className="text-blue-500 hover:text-blue-700 transition-colors"
              title={showExpenseDetails 
                ? t('dashboard.totalExpensesCard.hideDetails', 'Hide details')
                : t('dashboard.totalExpensesCard.showDetails', 'Show sharing space details')
              }
            >
              <Calculator className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsAnnual(false)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              !isAnnual 
                ? 'bg-[#253985] text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('common.monthly', 'Monthly')}
          </button>
          <button
            onClick={() => setIsAnnual(true)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              isAnnual 
                ? 'bg-[#253985] text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('common.annual', 'Annual')}
          </button>
        </div>
      </div>

      {/* Estimated expense details tooltip */}
      {showExpenseDetails && currentNetAdjustment !== 0 && (
        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm animate-fadeIn">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-blue-800 flex items-center gap-1">
              <Users className="w-4 h-4" />
              {t('dashboard.totalExpensesCard.estimatedAdjustments', 'ShareSubs Estimated Adjustments')}
            </span>
            <span className={`font-semibold ${hasNetSaving ? 'text-green-600' : 'text-red-600'}`}>
              {hasNetSaving 
                ? `-${formatCurrency(currentNetAdjustment)}` 
                : `+${formatCurrency(absoluteAdjustment)}`
              }
            </span>
          </div>
          {currentTotalSavings > 0 && (
            <div className="flex items-center justify-between text-green-600 mb-1">
              <div className="flex items-center gap-1">
                <TrendingDown className="w-3 h-3" />
                <span>{t('dashboard.totalExpensesCard.expectedSavingsAsAdmin', 'Expected Savings as Admin')}</span>
              </div>
              <span className="font-medium">-{formatCurrency(currentTotalSavings)}</span>
            </div>
          )}
          {currentTotalAdditionalCost > 0 && (
            <div className="flex items-center justify-between text-red-600 mb-1">
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                <span>{t('dashboard.totalExpensesCard.expectedCostAsMember', 'Expected Cost as Member')}</span>
              </div>
              <span className="font-medium">+{formatCurrency(currentTotalAdditionalCost)}</span>
            </div>
          )}
          {sharingSpacesCount > 0 && (
            <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-blue-100">
              {t('dashboard.totalExpensesCard.basedOnExpectedCosts', 'Based on expected costs across all sharing spaces')}
              
            </div>
          )}
        </div>
      )}

      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold text-[#253985]">
          €{currentTotal.toFixed(2)}
        </span>
        <span className="text-sm text-gray-500 mb-1">
          {isAnnual 
            ? t('dashboard.totalExpensesCard.perYear', '/ year')
            : t('dashboard.totalExpensesCard.perMonth', '/ month')
          }
        </span>
      </div>

      {/* Original total comparison with sharing space effect */}
      {currentNetAdjustment !== 0 && (
        <div className="text-sm text-gray-500">
          <div className="flex items-center justify-between mb-1">
            <span>{t('dashboard.totalExpensesCard.personalSubscriptions', 'Personal subscriptions')}:</span>
            <span>€{currentOriginalTotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              <span>{t('dashboard.totalExpensesCard.shareSubsEstimatedAdjustment', 'ShareSubs estimated adjustment')}:</span>
            </span>
            <span className={`font-medium ${hasNetSaving ? 'text-green-600' : 'text-red-600'}`}>
              {hasNetSaving ? '-' : '+'}€{absoluteAdjustment.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      <div className="h-[1px] bg-gray-200 w-full my-3"></div>

      <div className="grid grid-cols-4 gap-4 text-sm">
        <div className="text-center">
          <p className="text-gray-500">{t('dashboard.totalExpensesCard.active', 'Active')}</p>
          <p className="font-medium text-[#253985]">{activeSubscriptions}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-500">{t('dashboard.totalExpensesCard.freeTrials', 'Free Trials')}</p>
          <p className="font-medium text-[#253985]">{activeFreeTrialsCount}</p>
        </div>
        <div className="text-center">
          <p className="text-gray-500">{t('dashboard.totalExpensesCard.avgCost', 'Avg. Cost')}</p>
          <p className="font-medium text-[#253985]">
            €{currentAvgCost.toFixed(2)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-gray-500 flex items-center justify-center gap-1">
            <Users className="w-3 h-3" />
            {t('dashboard.totalExpensesCard.shareSubs', 'ShareSubs')}
          </p>
          <p className={`font-medium ${hasNetSaving ? 'text-green-600' : 'text-red-600'}`}>
            {hasNetSaving 
              ? `${t('dashboard.totalExpensesCard.saving', 'Saving')}: €${absoluteAdjustment.toFixed(2)}`
              : `${t('dashboard.totalExpensesCard.cost', 'Cost')}: €${absoluteAdjustment.toFixed(2)}`
            }
          </p>
        </div>
      </div>

      {/* ShareSubs participation summary */}
      {sharingSpacesCount > 0 && (
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <div className={`w-2 h-2 rounded-full ${hasNetSaving ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span>{getSummaryText()}</span>
            </div>
            <button
              onClick={handleManageClick}
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              {t('dashboard.totalExpensesCard.manage', 'Manage')}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {t('dashboard.totalExpensesCard.basedOnPerPerson', 'Based on expected costs per person in each space')}
            
          </div>
        </div>
      )}
    </div>
  );
};

export default TotalExpensesCard;